/**
 * Load status transitions - ONE map shared by the details modal, the Kanban
 * drag handler and anything else that writes loads.status from the board.
 *
 * Rules:
 *  - ALLOWED lists the normal next steps (forward moves plus the one-step
 *    back-outs dispatch uses to correct a driver/geofence mistake).
 *  - Anything else is an OVERRIDE: callers must ask the dispatcher to confirm.
 *  - Writes go through updateLoadStatusGuarded(): the UPDATE is conditioned on
 *    the status the caller last saw, so a concurrent write from the driver app
 *    or the geofence engine is never silently overwritten (audit S15).
 */

import { supabase } from '../supabaseClient';

export const ALLOWED = {
  pending:            ['assigned', 'en_route', 'cancelled'],
  assigned:           ['en_route', 'at_pickup', 'in_transit', 'pending', 'cancelled'],
  en_route:           ['at_pickup', 'loading', 'in_transit', 'assigned', 'cancelled'],
  at_pickup:          ['loading', 'in_transit', 'en_route'],
  loading:            ['in_transit', 'at_delivery', 'at_pickup'],
  in_transit:         ['at_delivery', 'unloading', 'loading'],
  at_delivery:        ['unloading', 'pending_completion', 'in_transit'],
  unloading:          ['pending_completion', 'complete', 'at_delivery'],
  pending_completion: ['complete', 'unloading', 'at_delivery'],
  complete:           ['pending_completion'],
  // __RTB_RETIRED__ ready_to_bill is retired (Henry 2026-09-30: a complete job IS ready
  // to bill; the hub's Hauling Board tab bills 'complete'). Kept as a FROM state only,
  // so an old row can still be moved on; nothing on the board moves a load INTO it.
  ready_to_bill:      ['complete', 'billed'],
  billed:             ['archived', 'complete'],
  archived:           [],
  cancelled:          ['pending'],
};

export const LABELS = {
  pending: 'Pending', assigned: 'Assigned', en_route: 'En Route', at_pickup: 'At Pickup',
  loading: 'Loading', in_transit: 'In Transit', at_delivery: 'At Delivery', unloading: 'Unloading',
  pending_completion: 'Confirm Complete', complete: 'Complete', ready_to_bill: 'Complete (was Ready to Bill)',
  billed: 'Billed', archived: 'Archived', cancelled: 'Cancelled',
};

export const labelOf = (s) => LABELS[s] || (s || '').replace(/_/g, ' ');

/**
 * Statuses that are no longer used. The DB CHECK (loads_status_check) still accepts
 * them so old rows stay valid, but the board never writes a load INTO one: no column,
 * no modal button, no transition, and updateLoadStatusGuarded refuses it.
 */
export const RETIRED_STATUSES = ['ready_to_bill'];
export const isRetiredStatus = (s) => RETIRED_STATUSES.includes(s);
/** The live status an old row counts as (ready_to_bill -> complete). */
export const liveStatusOf = (s) => (s === 'ready_to_bill' ? 'complete' : s);

/** true when to is a normal next step from from. Same status is never a transition. */
export const canTransition = (from, to) =>
  !!from && !!to && from !== to && !isRetiredStatus(to) && (ALLOWED[from] || []).includes(to);

/**
 * Conditional status write.
 * @returns {{ok:true}} | {{ok:false, reason:'conflict'|'error', current?:string, error?:any}}
 *   conflict = the row is no longer in expectedStatus (someone else moved it);
 *   `current` carries what it is now so the UI can say so.
 */
/**
 * __CARGO_REQUIRED__ (2026-09-30) the database (trigger loads_cargo_before_completion) refuses to
 * complete a job that has no cargo type, or whose driver has not answered how much time a wrong
 * address cost. Turns that refusal into a sentence a dispatcher can act on; null for any other error.
 */
export const completionBlockText = (err) => {
  const m = String((err && (err.message || err.details || err)) || '');
  if (!/CARGO_REQUIRED|TIME_LOST_REQUIRED/.test(m)) return null;
  const parts = [];
  if (/CARGO_REQUIRED/.test(m)) {
    parts.push("the cargo type is not set (use Edit on this load, the hub's Saved Jobs or Hauling Board tab, or the Driver app)");
  }
  if (/TIME_LOST_REQUIRED/.test(m)) {
    parts.push("the driver has not answered how much time a wrong address cost (he answers in the Driver app - None counts; the office can enter it in the hub's Hauling Board tab delay breakdown)");
  }
  return 'Cannot complete this job yet: ' + parts.join(', and ') + '.';
};

export const updateLoadStatusGuarded = async (loadId, expectedStatus, newStatus, extra = {}) => {
  if (isRetiredStatus(newStatus)) {
    return { ok: false, reason: 'error', error: { message: `"${labelOf(newStatus)}" is no longer used - a complete job is already ready to bill.` } };
  }
  try {
    const { data, error } = await supabase
      .from('loads')
      .update({ status: newStatus, ...extra })
      .eq('id', loadId)
      .eq('status', expectedStatus)
      .select('id');
    if (error) return { ok: false, reason: completionBlockText(error) ? 'blocked' : 'error', error, message: completionBlockText(error) };
    if (data && data.length > 0) return { ok: true };
    const { data: fresh } = await supabase.from('loads').select('status').eq('id', loadId).single();
    return { ok: false, reason: 'conflict', current: fresh ? fresh.status : null };
  } catch (e) {
    return { ok: false, reason: 'error', error: e };
  }
};

export default { ALLOWED, LABELS, labelOf, canTransition, updateLoadStatusGuarded, completionBlockText, RETIRED_STATUSES, isRetiredStatus, liveStatusOf };
